import { useState, useEffect } from 'react'
import { fetchRows, createRow } from '../lib/api'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import DateField from '../components/DateField'

function fmtDate(d) {
  if (!d) return ''
  const m = new Date(d + 'T12:00:00')
  if (isNaN(m)) return d
  return m.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function Diary() {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ 'Entry Date': '', 'Title': '', 'Body': '', 'Location': '', 'Tags': '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    try {
      const data = await fetchRows('diary')
      setEntries([...(data || [])].reverse())
    } catch (e) { setEntries([]) }
    finally { setLoading(false) }
  }

  async function save() {
    if (!form['Body'] && !form['Title']) { alert('Add a title or some text.'); return }
    setSaving(true)
    try {
      await createRow('diary', {
        'Entry Date': form['Entry Date'] || new Date().toISOString().split('T')[0],
        'Title': form['Title'] || '',
        'Body': form['Body'] || '',
        'Location': form['Location'] || '',
        'Tags': form['Tags'] || '',
        'Created By': 'Captain',
      })
      setForm({ 'Entry Date': '', 'Title': '', 'Body': '', 'Location': '', 'Tags': '' })
      setShowForm(false)
      load()
    } catch (e) { alert('Could not save: ' + e.message) }
    finally { setSaving(false) }
  }

  // asks first; after the delete, Undo shows for 10 seconds (src/lib/undoDelete.js)
  async function remove(e) {
    const label = e['Title'] || fmtDate(e['Entry Date']) || 'diary entry'
    if (!window.confirm('Delete the diary entry "' + label + '"? You can undo it for 10 seconds.')) return
    try {
      await deleteWithUndo(supabase, { table: 'diary_entries', id: e.id, label: 'diary entry "' + label + '"', onUndone: () => load() })
      setEntries(prev => prev.filter(x => x.id !== e.id))
    } catch (err) { alert('Could not delete: ' + (err.message || err)) }
  }

  const filtered = entries.filter(e => {
    if (!query) return true
    const q = query.toLowerCase()
    return (e['Title'] || '').toLowerCase().includes(q) || (e['Body'] || '').toLowerCase().includes(q) || (e['Location'] || '').toLowerCase().includes(q) || (e['Tags'] || '').toLowerCase().includes(q)
  })

  return (
    <div className="tbl-wrap fade-up" style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input placeholder="Search the logbook..." value={query} onChange={e => setQuery(e.target.value)} style={{ flex: 1, background: 'var(--hull)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 13, padding: '9px 12px', borderRadius: 8, fontFamily: 'inherit' }} />
        <button className="btn btn-primary" onClick={() => setShowForm(v => !v)} style={{ fontSize: 12 }}>{showForm ? 'Close' : '+ New Entry'}</button>
      </div>

      {showForm && (
        <div style={{ background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 150 }}><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Date</label><DateField value={form['Entry Date']} onChange={v => setForm(f => ({ ...f, 'Entry Date': v || '' }))} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Location</label><input value={form['Location']} onChange={e => setForm(f => ({ ...f, 'Location': e.target.value }))} placeholder="e.g. Palma" style={{ width: '100%', background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} /></div>
          </div>
          <div><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Title</label><input value={form['Title']} onChange={e => setForm(f => ({ ...f, 'Title': e.target.value }))} style={{ width: '100%', background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} /></div>
          <div><label style={{ fontSize: 11, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Entry</label><textarea rows={4} value={form['Body']} onChange={e => setForm(f => ({ ...f, 'Body': e.target.value }))} style={{ width: '100%', background: 'var(--ink2)', border: '0.5px solid var(--border)', color: 'var(--text)', fontSize: 12, padding: '7px 10px', borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }} /></div>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ fontSize: 12, alignSelf: 'flex-end' }}>{saving ? 'Saving...' : 'Save Entry'}</button>
        </div>
      )}

      {loading && <div className="loading-row"><div className="spin" />Loading...</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', padding: 50 }}>No diary entries yet. Log one from here or ask the AI Assistant to "log today: ...".</div>
      )}
      {!loading && filtered.map((e, i) => (
        <div key={e.id || i} style={{ display: 'flex', gap: 16, paddingBottom: 20, marginBottom: 20, borderBottom: '0.5px solid var(--border)' }}>
          <div style={{ width: 92, flexShrink: 0, textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold2)' }}>{fmtDate(e['Entry Date'])}</div>
            {e['Location'] && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{e['Location']}</div>}
          </div>
          <div style={{ flex: 1 }}>
            {e['Title'] && <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{e['Title']}</div>}
            {e['Body'] && <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{e['Body']}</div>}
            {e['Tags'] && <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>{String(e['Tags']).split(',').map(t => t.trim()).filter(Boolean).map(t => <span key={t} style={{ fontSize: 9, padding: '2px 8px', borderRadius: 8, background: 'var(--chip)', color: 'var(--muted)' }}>{t}</span>)}</div>}
          </div>
          <button onClick={() => remove(e)} title="Delete this entry" aria-label="Delete this entry" style={{ background: 'none', border: 'none', color: '#b4313c', fontSize: 15, cursor: 'pointer', flexShrink: 0, height: 'fit-content' }}>×</button>
        </div>
      ))}
    </div>
  )
}
